import React, { useEffect, useRef } from 'react';
import { FaExclamationTriangle, FaUndo } from 'react-icons/fa';
import Poster from '../poster/Poster.component';
import {
  FeedSkeletonGrid,
  FeedErrorState,
  FeedEmptyState,
  FeedEndState,
  FEED_GRID,
} from './FeedStates.component';

// ---------------------------------------------------------------------------
// InfiniteFeed — vertically scrolling poster grid that keeps appending as the
// user reaches the bottom.
//
// Two independent triggers load the next page:
//   1. an IntersectionObserver sentinel (pre-loads ~600px early for smooth scrolling)
//   2. an explicit "Load More" button, because the app also runs on TV remotes and
//      D-pad navigation where there is no touch/scroll gesture to lean on
// Both are always available, so a missing IntersectionObserver degrades to the
// button rather than to a dead feed.
// ---------------------------------------------------------------------------

const InfiniteFeed = ({
  items = [],
  status = 'idle',
  error = null,
  hasMore = true,
  totalResults = 0,
  isInitialLoading = false,
  isLoadingMore = false,
  onLoadMore,
  onRetry,
  label = 'Content feed',
  noun = 'titles',
  renderItem,
  skeletonCount = 12,
  emptyState,
  gridClassName = FEED_GRID,
}) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || isInitialLoading) return undefined;

    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && onLoadMore) {
          onLoadMore();
        }
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isInitialLoading, onLoadMore]);

  if (isInitialLoading) {
    return (
      <div role="status" aria-busy="true" aria-label={`Loading ${noun}`}>
        <span className="sr-only">Loading {noun}…</span>
        <FeedSkeletonGrid count={skeletonCount} className={gridClassName} />
      </div>
    );
  }

  if (status === 'error' && items.length === 0) {
    return <FeedErrorState error={error} onRetry={onRetry} />;
  }

  if (items.length === 0) {
    return emptyState || (
      <FeedEmptyState
        title={`No ${noun} found`}
        description="Try adjusting the genre or rating filters, or check back later."
      />
    );
  }

  return (
    <div>
      <div className={gridClassName} role="feed" aria-busy={isLoadingMore} aria-label={label}>
        {items.map((item) =>
          renderItem ? (
            <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
          ) : (
            <Poster {...item} key={item.id} />
          )
        )}
      </div>

      {/* Append-page skeletons keep the scrollbar stable instead of jumping. */}
      {isLoadingMore && (
        <div role="status" aria-live="polite" className="mt-6">
          <span className="sr-only">Loading more {noun}…</span>
          <FeedSkeletonGrid count={6} className={gridClassName} />
        </div>
      )}

      {/* A failed page 2+ still has content on screen — surface it inline so the
          feed never looks frozen. */}
      {status === 'error' && (
        <div
          role="alert"
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center bg-dark-800/70 border border-red-500/20 rounded-2xl px-5 py-4"
        >
          <p className="text-xs text-gray-300 flex items-center gap-2">
            <FaExclamationTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" aria-hidden="true" />
            <span>Could not load more {noun}. Check your connection and try again.</span>
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-extrabold text-xs px-4 py-2 rounded-xl transition-all shadow-md"
            >
              <FaUndo className="w-3 h-3" aria-hidden="true" />
              <span>Retry</span>
            </button>
          )}
        </div>
      )}

      {!isLoadingMore && status !== 'error' && hasMore && (
        <div className="flex justify-center pt-10">
          <button
            type="button"
            onClick={onLoadMore}
            className="inline-flex items-center gap-2 bg-dark-700 hover:bg-dark-600 text-white font-bold text-xs sm:text-sm px-8 py-3 rounded-2xl border border-white/10 hover:border-accent-gold/40 transition-all shadow-xl"
          >
            <span>Load More</span>
          </button>
        </div>
      )}

      {!hasMore && <FeedEndState totalResults={totalResults} noun={noun} />}

      {/* IntersectionObserver sentinel — invisible, purely a scroll trigger. */}
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
    </div>
  );
};

export default InfiniteFeed;
